import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { DatasetIdentifier } from "api/model/dataset/datasetModels";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import React, { useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import T from "services/lang/LanguageService";
import { timeAgo } from "utils/time-utils";

interface IDatasetTitleProps {
  identifier: string | DatasetIdentifier | null;
  hideData?: boolean;
}

interface IRecentDataForTitle {
  time: string;
  value: number;
}

export default function DatasetTitle(props: IDatasetTitleProps) {
  const [recentData, setRecentData] = useState<IRecentDataForTitle>();
  const query = useGetRecentData([props.identifier as string], {});

  useEffect(() => {
    if (query.data !== undefined && query.data[0]?.data.length > 0) {
      const { key: time, value } = query.data[0].data[0];
      setRecentData({ time, value });
    }
  }, [query.data]);

  const loading = query.isLoading;
  const missing = props.identifier == null;

  const titleBody = useMemo(() => {
    if (missing) {
      return T("message.missing-dataset-identifier");
    }

    if (loading) {
      return <i>{props.identifier} ({T("word.loading")})</i>;
    }

    if (query.isError) {
      return `${props.identifier} - ${T("word.error")}: ${query.error?.message}`;
    }

    if (query.isSuccess) {
      const ago = timeAgo(recentData?.time);
      return (
        <>
          <span>{query.data[0].title} </span>
          <Tooltip id="recent-data" opacity={0.85} />
          <span data-tooltip-id="recent-data" data-tooltip-content={ago}>
            |
            {props.hideData
              ? ""
              : ` ${recentData?.value ? recentData?.value.toFixed(2) : "N/A"}`}{" "}
            {query.data[0].unit}
          </span>
        </>
      );
    }
  }, [missing, loading, query, recentData]);

  return (
    <span className="flex gap-1 align-items-center">
      <CopyTextWithButton text={titleBody as string} copyableText={props.identifier as string} />
      <ChartInDialogButton
        header={query.isSuccess ? query.data[0]?.title : T("word.loading")}
        identifier={props.identifier as string}
      />
    </span>
  );
}
